/**
 * Logger Module Exports
 * Central export point for all logging functionality
 */

// Core logger service
export { logger, LoggerService } from './Logger';

import type { LoggableData } from '@xanectech/types';

// Import the logger instance for use in helper functions
import { logger } from './Logger';

// Convenience functions for common logging patterns
export const createLogger = (context: string) => logger.scope(context);

// Performance helpers
export const timeAsync = async <T>(
  label: string,
  operation: () => Promise<T>,
  metadata?: Record<string, LoggableData>,
): Promise<T> => {
  logger.time(label, metadata);
  try {
    const result = await operation();
    logger.timeEnd(label, { success: true, ...metadata });
    return result;
  } catch (error) {
    logger.timeEnd(label, {
      success: false,
      error: error instanceof Error ? error.message : String(error),
      ...metadata,
    });
    throw error;
  }
};

export const timeSync = <T>(
  label: string,
  operation: () => T,
  metadata?: Record<string, LoggableData>,
): T => {
  logger.time(label, metadata);
  try {
    const result = operation();
    logger.timeEnd(label, { success: true, ...metadata });
    return result;
  } catch (error) {
    logger.timeEnd(label, {
      success: false,
      error: error instanceof Error ? error.message : String(error),
      ...metadata,
    });
    throw error;
  }
};

// Error boundary helper
export const logError = (
  error: Error,
  context: string,
  metadata?: Record<string, LoggableData>,
) => {
  logger.error(`Error in ${context}`, error, 'error');
  if (metadata) {
  }
};

// Development helpers
export const devLog = (message: string, data?: unknown) => {
  if (import.meta.env.VITE_DEV) {
    const metadata =
      data && typeof data === 'object' && !Array.isArray(data)
        ? (data as Record<string, LoggableData>)
        : data
          ? { data: String(data) }
          : undefined;
  }
};

// API request/response logging helpers
export const logApiRequest = (
  method: string,
  url: string,
  metadata?: Record<string, LoggableData>,
) => {
  logger.info(`API Request: ${method.toUpperCase()} ${url}`, metadata, 'api');
};

export const logApiResponse = (
  method: string,
  url: string,
  status: number,
  metadata?: Record<string, LoggableData>,
) => {
  const level = status >= 400 ? 'error' : status >= 300 ? 'warn' : 'info';
  logger[level](
    `API Response: ${method.toUpperCase()} ${url} - ${status}`,
    metadata,
    'api',
  );
};

export { getErrorMessage } from './Logger';
